import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AssessmentModal = ({ isOpen, onClose, service, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const assessmentQuestions = {
    'velocity-engineering': [
      {
        id: 'current-velocity',
        question: 'How would you rate your current development velocity?',
        type: 'radio',
        options: [
          { value: 'excellent', label: 'Excellent - Consistently meeting sprint goals' },
          { value: 'good', label: 'Good - Meeting most sprint goals' },
          { value: 'average', label: 'Average - Meeting about half of sprint goals' },
          { value: 'poor', label: 'Poor - Rarely meeting sprint goals' }
        ]
      },
      {
        id: 'technical-debt',
        question: 'What are your biggest technical debt challenges?',
        type: 'checkbox',
        options: [
          { value: 'legacy-code', label: 'Legacy code maintenance' },
          { value: 'testing-gaps', label: 'Insufficient test coverage' },
          { value: 'documentation', label: 'Poor documentation' },
          { value: 'architecture', label: 'Outdated architecture patterns' },
          { value: 'dependencies', label: 'Dependency management issues' }
        ]
      },
      {
        id: 'deployment-frequency',
        question: 'How often do you deploy to production?',
        type: 'radio',
        options: [
          { value: 'multiple-daily', label: 'Multiple times per day' },
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'monthly', label: 'Monthly or less' }
        ]
      }
    ],
    'ownership-culture': [
      {
        id: 'accountability-level',
        question: 'How would you describe accountability in your team?',
        type: 'radio',
        options: [
          { value: 'high', label: 'High - Team members take full ownership' },
          { value: 'moderate', label: 'Moderate - Some ownership, needs improvement' },
          { value: 'low', label: 'Low - Frequent blame-shifting and excuses' },
          { value: 'unclear', label: 'Unclear - Roles and responsibilities are fuzzy' }
        ]
      },
      {
        id: 'decision-making',
        question: 'What slows down your decision-making process?',
        type: 'checkbox',
        options: [
          { value: 'hierarchy', label: 'Too many approval layers' },
          { value: 'information', label: 'Lack of information' },
          { value: 'consensus', label: 'Difficulty reaching consensus' },
          { value: 'fear', label: 'Fear of making wrong decisions' },
          { value: 'communication', label: 'Poor communication channels' }
        ]
      },
      {
        id: 'team-dynamics',
        question: 'How effective is cross-team collaboration?',
        type: 'radio',
        options: [
          { value: 'excellent', label: 'Excellent - Seamless collaboration' },
          { value: 'good', label: 'Good - Generally works well' },
          { value: 'average', label: 'Average - Some friction but manageable' },
          { value: 'poor', label: 'Poor - Significant collaboration issues' }
        ]
      }
    ],
    'architectural-modernization': [
      {
        id: 'system-scalability',
        question: 'How well does your current architecture handle scale?',
        type: 'radio',
        options: [
          { value: 'excellent', label: 'Excellent - Scales seamlessly' },
          { value: 'good', label: 'Good - Handles current load well' },
          { value: 'struggling', label: 'Struggling - Performance issues under load' },
          { value: 'critical', label: 'Critical - Frequent outages and bottlenecks' }
        ]
      },
      {
        id: 'modernization-priorities',
        question: 'What are your top modernization priorities?',
        type: 'checkbox',
        options: [
          { value: 'microservices', label: 'Microservices architecture' },
          { value: 'cloud-native', label: 'Cloud-native transformation' },
          { value: 'api-first', label: 'API-first design' },
          { value: 'observability', label: 'Monitoring and observability' },
          { value: 'security', label: 'Security modernization' }
        ]
      },
      {
        id: 'technology-stack',
        question: 'How current is your technology stack?',
        type: 'radio',
        options: [
          { value: 'cutting-edge', label: 'Cutting-edge - Latest technologies' },
          { value: 'modern', label: 'Modern - Recent versions and patterns' },
          { value: 'mixed', label: 'Mixed - Some modern, some legacy' },
          { value: 'legacy', label: 'Legacy - Significantly outdated' }
        ]
      }
    ]
  };

  const questions = assessmentQuestions[service?.id] || [];

  const handleResponse = (questionId, value, isCheckbox = false) => {
    setResponses(prev => {
      if (isCheckbox) {
        const currentValues = prev[questionId] || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [questionId]: newValues };
      }
      return { ...prev, [questionId]: value };
    });
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    onComplete(responses);
    setIsSubmitting(false);
  };

  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === questions.length - 1;
  const canProceed = responses[currentQuestion?.id];

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {service.title} Assessment
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                Step {currentStep + 1} of {questions.length}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>
          
          <div className="mt-4">
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-accent h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {currentQuestion && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-text-primary">
                {currentQuestion.question}
              </h3>

              <div className="space-y-3">
                {currentQuestion.type === 'radio' && (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name={currentQuestion.id}
                          value={option.value}
                          checked={responses[currentQuestion.id] === option.value}
                          onChange={(e) => handleResponse(currentQuestion.id, e.target.value)}
                          className="w-4 h-4 text-accent"
                        />
                        <span className="text-text-primary">{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {currentQuestion.type === 'checkbox' && (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option) => (
                      <Checkbox
                        key={option.value}
                        label={option.label}
                        checked={(responses[currentQuestion.id] || []).includes(option.value)}
                        onChange={(e) => handleResponse(currentQuestion.id, option.value, true)}
                        className="p-3 rounded-lg border border-border hover:bg-muted"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border">
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Previous
            </Button>

            <Button
              variant="default"
              onClick={handleNext}
              disabled={!canProceed}
              loading={isSubmitting}
              iconName={isLastStep ? "Check" : "ChevronRight"}
              iconPosition="right"
              className="bg-accent hover:bg-accent/90"
            >
              {isLastStep ? 'Complete Assessment' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentModal;